// umlaut-check: skip — "BestaetigenModal" ist ein Komponenten-/Dateiname
// (technischer Bezeichner), bleibt ASCII.

import React, {useState} from "react";
import axios from "axios";
import {Modal, OverlayTrigger, Table, Tooltip} from "react-bootstrap";
import {FlexToast, useExternalControls} from "laravel-inertia-backend";
import HeaderWithHelp from "../SalesAnalysis/HeaderWithHelp.jsx";
import KwSelect from "./KwSelect.jsx";
import BestaetigenModal from "./BestaetigenModal.jsx";
import {friendlyErrorMessage} from "../SalesAnalysis/format.js";
import {formatKw} from "./format.js";
import {TOOLTIP_POPPER_CONFIG} from "./tooltipConfig.js";

/**
 * "Liefergelegenheiten" (PLAN.md Abschnitt 4.5/1b, cosmos_produktionsfenster):
 * die Kalenderwochen, in denen Cosmos tatsächlich anliefert - Anlegen über
 * ein kleines Modal, Entfernen mit Bestätigung statt confirm(). Hinweis auf
 * den anschließenden Rhythmus, mit dem nach der letzten gepflegten Woche
 * weitergezählt wird.
 */
export default function LiefergelegenheitenListe({liefergelegenheiten, canParameters, onGeaendert}) {
    const {addToast} = useExternalControls();
    const [neu, setNeu] = useState(null);
    const [entfernen, setEntfernen] = useState(null);
    const [speichert, setSpeichert] = useState(false);

    if (!liefergelegenheiten) {
        return null;
    }

    const oeffnenNeu = () => setNeu({kw: null, marke: "", bemerkung: ""});

    const speichern = () => {
        if (!neu.kw) {
            return;
        }
        setSpeichert(true);
        axios
            .post("/cosmos/daten/liefergelegenheiten", neu)
            .then(() => {
                addToast(<FlexToast variant="success" title="Cosmos" autohide message="Liefergelegenheit angelegt – wirkt nach der nächsten Berechnung." />);
                setNeu(null);
                onGeaendert?.();
            })
            .catch((e) => addToast(<FlexToast variant="danger" title="Cosmos" autohide={false} message={friendlyErrorMessage(e, "Die Liefergelegenheit konnte nicht angelegt werden.")} />))
            .finally(() => setSpeichert(false));
    };

    const wirklichEntfernen = () => {
        setSpeichert(true);
        axios
            .delete(`/cosmos/daten/liefergelegenheiten/${entfernen.id}`)
            .then(() => {
                setEntfernen(null);
                onGeaendert?.();
            })
            .catch((e) => addToast(<FlexToast variant="danger" title="Cosmos" autohide={false} message={friendlyErrorMessage(e, "Die Liefergelegenheit konnte nicht entfernt werden.")} />))
            .finally(() => setSpeichert(false));
    };

    return (
        <>
            <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="small text-muted">
                    Kalenderwochen, in denen Cosmos tatsächlich anliefert („DAP SNV“) – keine Bestellwochen. Danach zählt ein
                    fester Lieferrhythmus weiter (gepflegt unter „Annahmen und Stellschrauben“).
                </span>
                {canParameters && (
                    <OverlayTrigger flip popperConfig={TOOLTIP_POPPER_CONFIG} placement="left" overlay={<Tooltip id="cosmos-liefergelegenheit-anlegen">Öffnet ein Formular, um eine neue Liefergelegenheit anzulegen.</Tooltip>}>
                        <span>
                            <button type="button" className="btn btn-sm btn-outline-primary text-nowrap ms-2" onClick={oeffnenNeu}>
                                Liefergelegenheit anlegen
                            </button>
                        </span>
                    </OverlayTrigger>
                )}
            </div>

            {liefergelegenheiten.length > 0 && (
                <Table responsive size="sm" className="align-middle">
                    <thead>
                        <tr>
                            <th>
                                <HeaderWithHelp help="Kalenderwoche, in der Cosmos tatsächlich anliefert.">Kalenderwoche</HeaderWithHelp>
                            </th>
                            <th>
                                <HeaderWithHelp help="Für welche Marke die Liefergelegenheit gilt – leer bedeutet alle Marken.">Marke</HeaderWithHelp>
                            </th>
                            <th>
                                <HeaderWithHelp help="Freitext-Notiz zu dieser Liefergelegenheit.">Bemerkung</HeaderWithHelp>
                            </th>
                            <th>
                                <HeaderWithHelp help="Woher diese Angabe stammt.">Herkunft</HeaderWithHelp>
                            </th>
                            {canParameters && <th />}
                        </tr>
                    </thead>
                    <tbody>
                        {liefergelegenheiten.map((f) => (
                            <tr key={f.id}>
                                <td>{formatKw(f.kw)}</td>
                                <td>{f.marke || "alle Marken"}</td>
                                <td className="small text-muted">{f.bemerkung}</td>
                                <td className="small text-muted">{f.herkunft}</td>
                                {canParameters && (
                                    <td>
                                        <OverlayTrigger flip popperConfig={TOOLTIP_POPPER_CONFIG} placement="top" overlay={<Tooltip id={`cosmos-liefergelegenheit-entfernen-${f.id}`}>Entfernt diese Liefergelegenheit – wirkt nach der nächsten Berechnung, mit Bestätigung.</Tooltip>}>
                                            <span>
                                                <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setEntfernen(f)}>
                                                    Entfernen
                                                </button>
                                            </span>
                                        </OverlayTrigger>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}

            <Modal show={Boolean(neu)} onHide={() => setNeu(null)} centered aria-labelledby="cosmos-liefergelegenheit-title">
                <Modal.Header closeButton>
                    <Modal.Title id="cosmos-liefergelegenheit-title" style={{fontSize: 16}}>
                        Liefergelegenheit anlegen
                    </Modal.Title>
                </Modal.Header>
                {neu && (
                    <Modal.Body className="d-flex flex-column gap-2">
                        <div>
                            <HeaderWithHelp help="Die Kalenderwoche, in der Cosmos tatsächlich anliefert.">
                                <span className="form-label small mb-1 d-block">Kalenderwoche</span>
                            </HeaderWithHelp>
                            <KwSelect value={neu.kw} onChange={(v) => setNeu({...neu, kw: v})} allowEmpty={false} ariaLabel="Kalenderwoche" />
                        </div>
                        <div>
                            <HeaderWithHelp help="Schränkt die Liefergelegenheit auf eine Marke ein – leer gilt für alle Marken.">
                                <label className="form-label small mb-1" htmlFor="cosmos-liefergelegenheit-marke">
                                    Marke (leer = alle Marken)
                                </label>
                            </HeaderWithHelp>
                            <input
                                id="cosmos-liefergelegenheit-marke"
                                type="text"
                                className="form-control form-control-sm"
                                value={neu.marke}
                                onChange={(e) => setNeu({...neu, marke: e.target.value})}
                            />
                        </div>
                        <div>
                            <HeaderWithHelp help="Freitext-Notiz zu dieser Liefergelegenheit.">
                                <label className="form-label small mb-1" htmlFor="cosmos-liefergelegenheit-bemerkung">
                                    Bemerkung
                                </label>
                            </HeaderWithHelp>
                            <input
                                id="cosmos-liefergelegenheit-bemerkung"
                                type="text"
                                className="form-control form-control-sm"
                                value={neu.bemerkung}
                                onChange={(e) => setNeu({...neu, bemerkung: e.target.value})}
                            />
                        </div>
                    </Modal.Body>
                )}
                <Modal.Footer>
                    <OverlayTrigger flip popperConfig={TOOLTIP_POPPER_CONFIG} placement="top" overlay={<Tooltip id="cosmos-liefergelegenheit-abbrechen">Schließt das Formular, ohne zu speichern.</Tooltip>}>
                        <span>
                            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setNeu(null)}>
                                Abbrechen
                            </button>
                        </span>
                    </OverlayTrigger>
                    <OverlayTrigger flip popperConfig={TOOLTIP_POPPER_CONFIG} placement="top" overlay={<Tooltip id="cosmos-liefergelegenheit-speichern">Legt die Liefergelegenheit an – wirkt nach der nächsten Berechnung.</Tooltip>}>
                        <span>
                            <button type="button" className="btn btn-primary btn-sm" disabled={speichert || !neu?.kw} onClick={speichern}>
                                Speichern
                            </button>
                        </span>
                    </OverlayTrigger>
                </Modal.Footer>
            </Modal>

            <BestaetigenModal
                show={Boolean(entfernen)}
                title="Liefergelegenheit entfernen"
                confirmLabel="Entfernen"
                busy={speichert}
                onConfirm={wirklichEntfernen}
                onCancel={() => setEntfernen(null)}
            >
                Soll die Liefergelegenheit {entfernen ? formatKw(entfernen.kw) : ""} wirklich entfernt werden? Das wirkt sich erst nach der
                nächsten Berechnung auf die Zeitstrahlen aus.
            </BestaetigenModal>
        </>
    );
}
